import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { pluginApi } from '@/lib/api';
import { useApiMutation } from '@/hooks/useApi';
import { Loader2, Save, Coins, ShoppingCart, TrendingUp, Plus, Trash2, Edit } from 'lucide-react';
import { toast } from 'sonner';

export function EconomyPlugin({ serverId }: { serverId: string }) {
  const { data: plugins } = useQuery({
    queryKey: ['servers', serverId, 'plugins'],
    queryFn: () => pluginApi.getPlugins(serverId),
  });

  const economyPlugin = plugins?.find((p: any) => p.pluginId === 'economy');
  const isEnabled = economyPlugin?.enabled ?? false;

  const { mutate: togglePlugin } = useApiMutation(
    ({ enabled }: { enabled: boolean }) => 
      enabled ? pluginApi.enablePlugin(serverId, 'economy') : pluginApi.disablePlugin(serverId, 'economy'),
    { invalidateQueries: [['servers', serverId, 'plugins']] }
  );

  const { mutate: updateConfig, isPending: saving } = useApiMutation(
    (config: any) => pluginApi.updatePluginConfig(serverId, 'economy', config),
    { 
      onSuccess: () => toast.success('Configuration saved!'),
      invalidateQueries: [['servers', serverId, 'plugins']] 
    }
  );

  const [config, setConfig] = useState<any>({
    enabled: true,
    currencyName: 'coins',
    currencySymbol: '🪙',
    dailyAmount: 100,
    dailyCooldown: 86400, // 24 hours
    workMinAmount: 50,
    workMaxAmount: 150,
    workCooldown: 3600, // 1 hour
    startingBalance: 0,
    gameEarningsEnabled: true,
    gameRewardMultiplier: 1,
  });

  const [items, setItems] = useState<any[]>([]);
  const [showAddItem, setShowAddItem] = useState(false);
  const [newItem, setNewItem] = useState({
    name: '',
    description: '',
    price: 0,
    type: 'role',
    roleId: '',
    stock: null,
  });

  useEffect(() => {
    if (economyPlugin?.config) {
      setConfig({ ...config, ...economyPlugin.config });
    }
  }, [economyPlugin]);

  const handleSave = () => {
    updateConfig(config);
  };

  const handleAddItem = () => {
    // TODO: Implement API call to add item
    toast.success('Item added!');
    setItems([...items, { ...newItem, id: Date.now().toString() }]);
    setNewItem({
      name: '',
      description: '',
      price: 0,
      type: 'role',
      roleId: '',
      stock: null,
    });
    setShowAddItem(false);
  };

  if (!economyPlugin) return null;

  return (
    <div className="space-y-6 max-w-4xl mx-auto pb-12">
      <div className="glass-card p-6 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-yellow-500/20 rounded-xl">
            <Coins className="w-6 h-6 text-yellow-400" />
          </div>
          <div>
            <h3 className="font-bold text-xl">Economy System</h3>
            <p className="text-sm text-muted-foreground">
              Virtual currency, shop, and rewards for your community.
            </p>
          </div>
        </div>
        <Switch 
          checked={isEnabled} 
          onCheckedChange={(checked) => togglePlugin({ enabled: checked })}
        />
      </div>

      {!isEnabled ? (
        <div className="glass-card p-12 text-center space-y-4">
          <Coins className="w-12 h-12 text-muted-foreground mx-auto opacity-20" />
          <h4 className="text-lg font-medium">Plugin is Disabled</h4>
          <p className="text-muted-foreground max-w-md mx-auto">
            Enable the Economy plugin to start rewarding your community members with virtual currency.
          </p>
          <Button onClick={() => togglePlugin({ enabled: true })}>Enable Plugin</Button>
        </div>
      ) : (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
          {/* Currency Settings */}
          <section className="glass-card overflow-hidden">
            <div className="p-6 bg-white/5 border-b border-white/10 flex items-center gap-2">
              <Coins className="w-5 h-5 text-yellow-400" />
              <h4 className="font-semibold">Currency Settings</h4>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Currency Name</Label>
                  <Input 
                    value={config.currencyName}
                    onChange={(e) => setConfig({...config, currencyName: e.target.value})}
                    placeholder="coins"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Currency Symbol</Label>
                  <Input 
                    value={config.currencySymbol}
                    onChange={(e) => setConfig({...config, currencySymbol: e.target.value})}
                    placeholder="🪙"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Starting Balance</Label>
                <Input 
                  type="number"
                  value={config.startingBalance}
                  onChange={(e) => setConfig({...config, startingBalance: parseInt(e.target.value)})}
                  placeholder="0"
                />
                <p className="text-xs text-muted-foreground">
                  Amount of currency new members start with
                </p>
              </div>
            </div>
          </section>

          {/* Daily Reward */}
          <section className="glass-card overflow-hidden">
            <div className="p-6 bg-white/5 border-b border-white/10 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-green-400" />
              <h4 className="font-semibold">Daily Reward</h4>
            </div>
            <div className="p-6 space-y-4">
              <div className="space-y-2">
                <Label>Daily Amount</Label>
                <Input 
                  type="number"
                  value={config.dailyAmount}
                  onChange={(e) => setConfig({...config, dailyAmount: parseInt(e.target.value)})}
                  placeholder="100"
                />
                <p className="text-xs text-muted-foreground">
                  Amount users receive with /daily command
                </p>
              </div>
              <div className="space-y-2">
                <Label>Cooldown (seconds)</Label>
                <Input 
                  type="number"
                  value={config.dailyCooldown}
                  onChange={(e) => setConfig({...config, dailyCooldown: parseInt(e.target.value)})}
                  placeholder="86400"
                />
                <p className="text-xs text-muted-foreground">
                  86400 = 24 hours
                </p>
              </div>
            </div>
          </section>

          {/* Work Command */}
          <section className="glass-card overflow-hidden">
            <div className="p-6 bg-white/5 border-b border-white/10 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-400" />
              <h4 className="font-semibold">Work Command</h4>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Min Amount</Label>
                  <Input 
                    type="number"
                    value={config.workMinAmount}
                    onChange={(e) => setConfig({...config, workMinAmount: parseInt(e.target.value)})}
                    placeholder="50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Max Amount</Label>
                  <Input 
                    type="number"
                    value={config.workMaxAmount}
                    onChange={(e) => setConfig({...config, workMaxAmount: parseInt(e.target.value)})}
                    placeholder="150"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Cooldown (seconds)</Label>
                <Input 
                  type="number"
                  value={config.workCooldown}
                  onChange={(e) => setConfig({...config, workCooldown: parseInt(e.target.value)})}
                  placeholder="3600"
                />
                <p className="text-xs text-muted-foreground">
                  3600 = 1 hour
                </p>
              </div>
            </div>
          </section>

          {/* Game Earnings */}
          <section className="glass-card overflow-hidden">
            <div className="p-6 bg-white/5 border-b border-white/10 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-purple-400" />
              <h4 className="font-semibold">Guadagni tramite gioco</h4>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex items-center justify-between p-4 rounded-lg bg-white/5">
                <div>
                  <Label>Abilita guadagni da Games</Label>
                  <p className="text-xs text-muted-foreground">Consenti ricompense dai comandi /daily, /roll, /game</p>
                </div>
                <Switch
                  checked={config.gameEarningsEnabled}
                  onCheckedChange={(checked) => setConfig({ ...config, gameEarningsEnabled: checked })}
                />
              </div>
              <div className="space-y-2">
                <Label>Moltiplicatore ricompense</Label>
                <Input
                  type="number"
                  min="0.1"
                  max="5"
                  step="0.1"
                  value={config.gameRewardMultiplier}
                  onChange={(e) => setConfig({ ...config, gameRewardMultiplier: parseFloat(e.target.value) || 1 })}
                />
                <p className="text-xs text-muted-foreground">
                  1 = normale, 2 = doppio, 0.5 = metà ricompense
                </p>
              </div>
            </div>
          </section>

          {/* Shop Items */}
          <section className="glass-card overflow-hidden">
            <div className="p-6 bg-white/5 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ShoppingCart className="w-5 h-5 text-purple-400" />
                <h4 className="font-semibold">Shop Items</h4>
              </div>
              <Button size="sm" onClick={() => setShowAddItem(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Add Item
              </Button>
            </div>
            <div className="p-6 space-y-4">
              {items.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <ShoppingCart className="w-12 h-12 mx-auto opacity-20 mb-4" />
                  <p>No items in shop yet</p>
                  <p className="text-sm">Add items that users can purchase with their currency</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {items.map((item) => (
                    <div key={item.id} className="flex items-center justify-between p-4 bg-white/5 rounded-lg">
                      <div>
                        <h5 className="font-medium">{item.name}</h5>
                        <p className="text-sm text-muted-foreground">{item.description}</p>
                        <p className="text-sm text-yellow-400 mt-1">
                          {config.currencySymbol} {item.price}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="ghost">
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-400">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* Add Item Modal */}
          {showAddItem && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="glass-card p-6 max-w-md w-full mx-4 space-y-4">
                <h4 className="font-semibold text-lg">Add Shop Item</h4>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Item Name</Label>
                    <Input 
                      value={newItem.name}
                      onChange={(e) => setNewItem({...newItem, name: e.target.value})}
                      placeholder="VIP Role"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Description</Label>
                    <Textarea 
                      value={newItem.description}
                      onChange={(e) => setNewItem({...newItem, description: e.target.value})}
                      placeholder="Get access to exclusive channels"
                      rows={3}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Price</Label>
                    <Input 
                      type="number"
                      value={newItem.price}
                      onChange={(e) => setNewItem({...newItem, price: parseInt(e.target.value)})}
                      placeholder="1000"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Type</Label>
                    <Select 
                      value={newItem.type}
                      onValueChange={(v) => setNewItem({...newItem, type: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="role">Role</SelectItem>
                        <SelectItem value="item">Item</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="flex gap-2 justify-end">
                  <Button variant="ghost" onClick={() => setShowAddItem(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleAddItem}>
                    Add Item
                  </Button>
                </div>
              </div>
            </div>
          )}

          <div className="flex justify-end pt-4">
            <Button size="lg" onClick={handleSave} disabled={saving} className="bg-yellow-600 hover:bg-yellow-700">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
